/* ===========================================================================
 * Budgets and Contracts (feature_key `contracts`) — formula engine.
 *
 * Plan: docs/plans/contracts-plan.md §6.3 (WP-23), R21, G7, G13, decision D6.
 *
 * D6: "One formula engine file, copied to the frontend, byte-identical by CI
 * test. Why: the backend cannot import TypeScript and the frontend build
 * cannot import outside src without config changes."
 *
 * THIS FILE EXISTS TWICE, BYTE FOR BYTE:
 *   backend/src/services/contracts/formula/engine.js   (the original)
 *   frontend/src/contracts/engine/engine.js            (the copy)
 * backend/tests/unit/contracts/engineCopy.test.js fails CI if the two differ.
 * Never hand-edit one side: edit the backend file and copy it over, so the
 * in-house grid (§8.1, R21) recomputes a cell exactly the way the backend
 * recompute-and-diff (§6.3, recompute.js) did. Drift here is a silent wrong
 * number in front of a negotiator, which is why the copy is machine-checked.
 *
 * Because it runs in BOTH jest-ESM (node 20) and the CRA browser bundle it is
 * pure, dependency-free, environment-neutral ECMAScript: no `node:` imports,
 * no DOM, no `process`, no `Buffer`, no timers, no I/O, no global state. Every
 * function is deterministic in its arguments.
 *
 * WHAT IT DOES (plan §6.3, verbatim scope):
 *   "grammar for numbers, strings, cell and range references including
 *    cross-sheet and absolute forms, operators + - * / ^ and percent,
 *    comparison operators, functions SUM, ROUND, ROUNDUP, ROUNDDOWN, IF, MIN,
 *    MAX, AVERAGE, COUNT, COUNTA, ABS, PRODUCT, SUMPRODUCT, AND, OR, NOT,
 *    IFERROR, VLOOKUP and INDEX/MATCH over in-workbook ranges. Anything else
 *    returns status unsupported for that cell with the function name recorded
 *    in the inventory; the cached value is used and labelled extracted; the
 *    cell is never guessed (G13)."
 *
 * WHAT IT REFUSES TO DO (G3, G13 — unknowns stay visible, failures are loud):
 *   Every construct the grammar does not cover comes back as
 *   `status: 'unsupported'` naming the construct — an unlisted function, an
 *   external-workbook reference, a 3-D sheet range, an array (CSE) formula,
 *   array arithmetic between ranges, an unresolved defined name, or a syntax
 *   the parser cannot read. It NEVER falls back to a guess, a zero, or a
 *   partial evaluation. Callers (recompute.js) then keep the workbook's own
 *   cached value and label it extracted (G8).
 *
 * VALUE MODEL. Internally a value is a native number, string or boolean, or
 * `null` for a blank cell, or an error box `{ __error: '#REF!' }` for one of
 * the seven Excel error values, or a range box `{ __range: {...} }` (only ever
 * produced by a reference and only ever consumed by a function that takes
 * ranges). `cellStoredValue()` maps a §5.5 `cta_cells` row onto that model.
 * =========================================================================== */

/** Bump when evaluation semantics change; recompute.js records it as provenance (G7). */
export const FORMULA_ENGINE_VERSION = '1.0.0';

/** The seven Excel error values, as Excel spells them. */
export const ERROR_VALUES = Object.freeze([
  '#NULL!',
  '#DIV/0!',
  '#VALUE!',
  '#REF!',
  '#NAME?',
  '#NUM!',
  '#N/A',
]);

/** Exactly the function set plan §6.3 lists. Anything else is `unsupported`. */
export const SUPPORTED_FUNCTIONS = Object.freeze([
  'ABS',
  'AND',
  'AVERAGE',
  'COUNT',
  'COUNTA',
  'IF',
  'IFERROR',
  'INDEX',
  'MATCH',
  'MAX',
  'MIN',
  'NOT',
  'OR',
  'PRODUCT',
  'ROUND',
  'ROUNDDOWN',
  'ROUNDUP',
  'SUM',
  'SUMPRODUCT',
  'VLOOKUP',
]);

/**
 * The subset of SUPPORTED_FUNCTIONS that folds a range into one number. Total
 * detection and the sum_range_short / dropped_column_summed detectors in
 * recompute.js ask "is this cell an aggregate?" through this list.
 */
export const AGGREGATE_FUNCTIONS = Object.freeze([
  'AVERAGE',
  'COUNT',
  'COUNTA',
  'MAX',
  'MIN',
  'PRODUCT',
  'SUM',
  'SUMPRODUCT',
]);

/* ---------------------------------------------------------------------------
 * 1. Typed errors (G13: a failure names the file, the location and the reason)
 * ------------------------------------------------------------------------ */

/**
 * Thrown when the engine is MISUSED — a malformed argument, a column index
 * that is not a positive integer, a workbook description that is not shaped
 * the way §5.5 shapes it. A formula the engine merely cannot evaluate is NOT
 * an exception: it is a returned result with `status: 'unsupported'` or
 * `status: 'error'`, so one bad cell never aborts a whole parse run.
 */
export class FormulaEngineError extends Error {
  constructor(message, details) {
    super(message);
    const d = details || {};
    this.name = 'FormulaEngineError';
    this.code = d.code || 'CTA_FORMULA_ENGINE';
    this.file = d.file === undefined ? null : d.file;
    this.location = d.location === undefined ? null : d.location;
    this.reason = d.reason === undefined ? message : d.reason;
  }
}

/** A formula the tokenizer or parser could not read. Carries the position. */
export class FormulaParseError extends FormulaEngineError {
  constructor(message, details) {
    super(message, Object.assign({ code: 'CTA_FORMULA_PARSE' }, details || {}));
    this.name = 'FormulaParseError';
    const d = details || {};
    this.position = d.position === undefined ? null : d.position;
    this.formula = d.formula === undefined ? null : d.formula;
  }
}

/* Internal control-flow signals. Not exported: they never escape the module. */

function ErrorSignal(errorValue) {
  this.errorValue = errorValue;
}

function UnsupportedSignal(item) {
  this.item = item;
}

function CycleSignal(path) {
  this.path = path;
}

function raiseError(errorValue) {
  throw new ErrorSignal(errorValue);
}

function raiseUnsupported(kind, name, detail) {
  throw new UnsupportedSignal({
    kind: kind,
    name: name === undefined ? null : name,
    detail: detail === undefined ? null : detail,
  });
}

/* ---------------------------------------------------------------------------
 * 2. A1 reference helpers (shared with recompute.js, WP-22's reader and the
 *    in-house grid: one spelling of "column C is index 3" for all of them)
 * ------------------------------------------------------------------------ */

/** 'A' -> 1, 'Z' -> 26, 'AA' -> 27. Returns null for anything else. */
export function columnLetterToIndex(letters) {
  if (typeof letters !== 'string' || letters.length === 0 || letters.length > 3) return null;
  let n = 0;
  for (let i = 0; i < letters.length; i += 1) {
    const c = letters.charCodeAt(i);
    let v;
    if (c >= 65 && c <= 90) v = c - 64;
    else if (c >= 97 && c <= 122) v = c - 96;
    else return null;
    n = n * 26 + v;
  }
  return n;
}

/** 1 -> 'A', 27 -> 'AA'. Throws (G13) rather than inventing a column. */
export function columnIndexToLetter(index) {
  if (typeof index !== 'number' || !isFinite(index) || Math.floor(index) !== index || index < 1) {
    throw new FormulaEngineError(
      'column index must be a positive integer, received ' + String(index),
      { code: 'CTA_FORMULA_BAD_COLUMN', reason: 'column index must be a positive integer' }
    );
  }
  let n = index;
  let out = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

/** (row, col) -> 'C90'. */
export function formatCellRef(row, col) {
  if (typeof row !== 'number' || !isFinite(row) || Math.floor(row) !== row || row < 1) {
    throw new FormulaEngineError(
      'row must be a positive integer, received ' + String(row),
      { code: 'CTA_FORMULA_BAD_ROW', reason: 'row must be a positive integer' }
    );
  }
  return columnIndexToLetter(col) + String(row);
}

const RE_STANDALONE_CELL = /^(?:(?:'((?:[^']|'')*)'|([^'!]+))!)?(\$?)([A-Za-z]{1,3})(\$?)([0-9]{1,7})$/;

/**
 * Parse a single A1 reference, with or without a sheet prefix and with or
 * without `$` anchors: 'C90', '$C$90', 'Sheet1!C90', "'Per Subject '!C90".
 * Returns { sheet, row, col, absRow, absCol } or null when the text is not a
 * single cell reference (a range, a name, anything else).
 */
export function parseCellRef(text) {
  if (typeof text !== 'string') return null;
  const m = RE_STANDALONE_CELL.exec(text.trim());
  if (!m) return null;
  const col = columnLetterToIndex(m[4]);
  if (col === null) return null;
  const row = parseInt(m[6], 10);
  if (!(row >= 1)) return null;
  let sheet = null;
  if (m[1] !== undefined) sheet = m[1].replace(/''/g, "'");
  else if (m[2] !== undefined) sheet = m[2];
  return {
    sheet: sheet,
    row: row,
    col: col,
    absRow: m[5] === '$',
    absCol: m[3] === '$',
  };
}

/**
 * Parse 'A1:B7' / "'Sheet 1'!A1:B7" into a normalised
 * { sheet, r1, c1, r2, c2 } with r1<=r2 and c1<=c2. Whole-column ('A:A') and
 * whole-row ('1:1') forms return null rows / null cols respectively; they are
 * only resolvable against a sheet extent (see createWorkbookContext). Returns
 * null when the text is not a range.
 */
export function parseRangeRef(text) {
  if (typeof text !== 'string') return null;
  const trimmed = text.trim();
  const bang = findSheetSplit(trimmed);
  let sheet = null;
  let body = trimmed;
  if (bang !== null) {
    sheet = bang.sheet;
    body = bang.rest;
  }
  const parts = body.split(':');
  if (parts.length !== 2) return null;
  const a = parseCellRef(parts[0]);
  const b = parseCellRef(parts[1]);
  if (a && b) {
    return normaliseRange(sheet, a.row, a.col, b.row, b.col);
  }
  const ca = columnLetterToIndex(parts[0].replace(/\$/g, ''));
  const cb = columnLetterToIndex(parts[1].replace(/\$/g, ''));
  if (ca !== null && cb !== null) {
    return { sheet: sheet, r1: null, c1: Math.min(ca, cb), r2: null, c2: Math.max(ca, cb) };
  }
  const ra = /^\$?([0-9]{1,7})$/.exec(parts[0]);
  const rb = /^\$?([0-9]{1,7})$/.exec(parts[1]);
  if (ra && rb) {
    const r1 = parseInt(ra[1], 10);
    const r2 = parseInt(rb[1], 10);
    return { sheet: sheet, r1: Math.min(r1, r2), c1: null, r2: Math.max(r1, r2), c2: null };
  }
  return null;
}

function findSheetSplit(text) {
  if (text.charAt(0) === "'") {
    let i = 1;
    let name = '';
    while (i < text.length) {
      if (text.charAt(i) === "'") {
        if (text.charAt(i + 1) === "'") {
          name += "'";
          i += 2;
          continue;
        }
        break;
      }
      name += text.charAt(i);
      i += 1;
    }
    if (text.charAt(i) !== "'" || text.charAt(i + 1) !== '!') return null;
    return { sheet: name, rest: text.slice(i + 2) };
  }
  const idx = text.indexOf('!');
  if (idx <= 0) return null;
  return { sheet: text.slice(0, idx), rest: text.slice(idx + 1) };
}

function normaliseRange(sheet, rowA, colA, rowB, colB) {
  return {
    sheet: sheet,
    r1: Math.min(rowA, rowB),
    c1: Math.min(colA, colB),
    r2: Math.max(rowA, rowB),
    c2: Math.max(colA, colB),
  };
}

/**
 * Enumerate a resolved range as [{ row, col }, ...] (row-major). `limit`
 * guards against a whole-sheet range: above it the call throws (G13) rather
 * than quietly truncating the list a caller is about to reason about.
 */
export function expandRange(range, limit) {
  if (!range || range.r1 === null || range.c1 === null || range.r2 === null || range.c2 === null) {
    throw new FormulaEngineError(
      'expandRange needs concrete rows and columns: resolve the whole-column or whole-row reference first',
      { code: 'CTA_FORMULA_OPEN_RANGE', reason: 'whole-column and whole-row ranges must be resolved first' }
    );
  }
  const max = typeof limit === 'number' ? limit : 1048576;
  const height = range.r2 - range.r1 + 1;
  const width = range.c2 - range.c1 + 1;
  if (height * width > max) {
    throw new FormulaEngineError(
      'range covers ' + String(height * width) + ' cells, above the limit of ' + String(max),
      { code: 'CTA_FORMULA_RANGE_TOO_BIG', reason: 'range too large to enumerate' }
    );
  }
  const out = [];
  for (let r = range.r1; r <= range.r2; r += 1) {
    for (let c = range.c1; c <= range.c2; c += 1) out.push({ row: r, col: c });
  }
  return out;
}

/** True when `value` is one of the seven Excel error strings. */
export function isErrorValue(value) {
  return typeof value === 'string' && ERROR_VALUES.indexOf(value) !== -1;
}

function makeError(errorValue) {
  return { __error: errorValue };
}

function isErrorBox(v) {
  return v !== null && typeof v === 'object' && typeof v.__error === 'string';
}

function isRangeBox(v) {
  return v !== null && typeof v === 'object' && v.__range !== undefined && v.__range !== null;
}

/* ---------------------------------------------------------------------------
 * 3. Tokenizer
 * ------------------------------------------------------------------------ */

const RE_WS = /[ \t\r\n]+/y;
const RE_NUMBER = /(?:[0-9]+(?:\.[0-9]*)?|\.[0-9]+)(?:[eE][+-]?[0-9]+)?/y;
const RE_ERRVAL = /#(?:NULL!|DIV\/0!|VALUE!|REF!|NAME\?|NUM!|N\/A)/y;
const RE_BOOL = /(?:TRUE|FALSE)(?![A-Za-z0-9_.])/iy;
const RE_QUOTED_SHEET = /'((?:[^']|'')*)'!/y;
const RE_BARE_SHEET = /([A-Za-z0-9_.\u00C0-\uFFFF]+)!/y;
const RE_CELL = /(\$?)([A-Za-z]{1,3})(\$?)([0-9]{1,7})(?![A-Za-z0-9_.])/y;
const RE_COL = /\$?([A-Za-z]{1,3})(?![A-Za-z0-9_.])/y;
const RE_ROWRANGE = /\$?([0-9]{1,7}):\$?([0-9]{1,7})(?![A-Za-z0-9_.])/y;
const RE_NAME = /[A-Za-z_\u00C0-\uFFFF][A-Za-z0-9_.\u00C0-\uFFFF]*/y;
const RE_EXTERNAL = /\[[^\]]*\][A-Za-z0-9_. \u00C0-\uFFFF]*!?(?:\$?[A-Za-z]{1,3}\$?[0-9]{1,7}(?::\$?[A-Za-z]{1,3}\$?[0-9]{1,7})?)?/y;

const TWO_CHAR_OPS = ['<=', '>=', '<>'];
const ONE_CHAR_OPS = ['+', '-', '*', '/', '^', '&', '=', '<', '>', '%'];

function matchAt(re, src, pos) {
  re.lastIndex = pos;
  return re.exec(src);
}

/**
 * Tokenize a formula body (no leading '='). Exported because the ref_error
 * detector needs to see a `#REF!` that is a real error literal and NOT one
 * that happens to sit inside a string literal such as `="see #REF! note"`
 * (wave-0/1 lesson: never regex raw text when a tokenizer is available).
 *
 * Token: { type, value, start, end } where type is one of
 * 'number' | 'string' | 'bool' | 'error' | 'ref' | 'range' | 'name' |
 * 'external' | 'sheet3d' | 'op' | 'lparen' | 'rparen' | 'comma'.
 */
export function tokenizeFormula(text) {
  if (typeof text !== 'string') {
    throw new FormulaEngineError('formula must be a string, received ' + typeof text, {
      code: 'CTA_FORMULA_BAD_INPUT',
      reason: 'formula must be a string',
    });
  }
  const src = text;
  const tokens = [];
  let i = 0;
  while (i < src.length) {
    const ws = matchAt(RE_WS, src, i);
    if (ws) {
      i = RE_WS.lastIndex;
      continue;
    }
    const ch = src.charAt(i);

    if (ch === '(') { tokens.push({ type: 'lparen', value: '(', start: i, end: i + 1 }); i += 1; continue; }
    if (ch === ')') { tokens.push({ type: 'rparen', value: ')', start: i, end: i + 1 }); i += 1; continue; }
    if (ch === ',' || ch === ';') { tokens.push({ type: 'comma', value: ',', start: i, end: i + 1 }); i += 1; continue; }

    if (ch === '"') {
      let j = i + 1;
      let out = '';
      let closed = false;
      while (j < src.length) {
        if (src.charAt(j) === '"') {
          if (src.charAt(j + 1) === '"') { out += '"'; j += 2; continue; }
          closed = true;
          j += 1;
          break;
        }
        out += src.charAt(j);
        j += 1;
      }
      if (!closed) {
        throw new FormulaParseError('unterminated string literal', { position: i, formula: text, reason: 'unterminated string literal' });
      }
      tokens.push({ type: 'string', value: out, start: i, end: j });
      i = j;
      continue;
    }

    if (ch === '#') {
      const e = matchAt(RE_ERRVAL, src, i);
      if (e) {
        tokens.push({ type: 'error', value: e[0], start: i, end: RE_ERRVAL.lastIndex });
        i = RE_ERRVAL.lastIndex;
        continue;
      }
      throw new FormulaParseError('unknown error literal at position ' + String(i), { position: i, formula: text, reason: 'unknown error literal' });
    }

    if (ch === '[') {
      const x = matchAt(RE_EXTERNAL, src, i);
      if (x) {
        tokens.push({ type: 'external', value: x[0], start: i, end: RE_EXTERNAL.lastIndex });
        i = RE_EXTERNAL.lastIndex;
        continue;
      }
    }

    if (ch >= '0' && ch <= '9') {
      const rr = matchAt(RE_ROWRANGE, src, i);
      if (rr) {
        tokens.push({ type: 'range', value: rr[0], start: i, end: RE_ROWRANGE.lastIndex });
        i = RE_ROWRANGE.lastIndex;
        continue;
      }
    }
    if ((ch >= '0' && ch <= '9') || (ch === '.' && src.charAt(i + 1) >= '0' && src.charAt(i + 1) <= '9')) {
      const n = matchAt(RE_NUMBER, src, i);
      if (n) {
        tokens.push({ type: 'number', value: parseFloat(n[0]), start: i, end: RE_NUMBER.lastIndex });
        i = RE_NUMBER.lastIndex;
        continue;
      }
    }

    const refTok = scanReference(src, i, text);
    if (refTok) {
      tokens.push(refTok);
      i = refTok.end;
      continue;
    }

    const two = src.substr(i, 2);
    if (TWO_CHAR_OPS.indexOf(two) !== -1) {
      tokens.push({ type: 'op', value: two, start: i, end: i + 2 });
      i += 2;
      continue;
    }
    if (ONE_CHAR_OPS.indexOf(ch) !== -1) {
      tokens.push({ type: 'op', value: ch, start: i, end: i + 1 });
      i += 1;
      continue;
    }

    throw new FormulaParseError(
      'unexpected character ' + JSON.stringify(ch) + ' at position ' + String(i),
      { position: i, formula: text, reason: 'unexpected character ' + JSON.stringify(ch) }
    );
  }
  return tokens;
}

/**
 * Scan an optional sheet prefix plus a cell reference, a range, or a bare
 * name at `pos`. Returns a token or null. A prefixed name ('Sheet1!Total') is
 * returned as a 'name' token carrying its sheet so the unsupported-scan can
 * report it by its full text.
 */
function scanReference(src, pos, whole) {
  let i = pos;
  let sheet = null;
  let sheetQuoted = false;

  if (src.charAt(i) === "'") {
    const q = matchAt(RE_QUOTED_SHEET, src, i);
    if (!q) return null;
    sheet = q[1].replace(/''/g, "'");
    sheetQuoted = true;
    i = RE_QUOTED_SHEET.lastIndex;
    if (sheet.charAt(0) === '[') {
      return { type: 'external', value: src.slice(pos, i), start: pos, end: i };
    }
    if (sheet.indexOf(':') !== -1) {
      return { type: 'sheet3d', value: src.slice(pos, i), start: pos, end: i };
    }
  } else {
    const b = matchAt(RE_BARE_SHEET, src, i);
    if (b) {
      // Only treat it as a sheet prefix when a reference body actually follows;
      // otherwise let it fall through (it cannot, since '!' is not an operator).
      sheet = b[1];
      i = RE_BARE_SHEET.lastIndex;
    }
  }

  const start = pos;

  // range: CELL ':' CELL
  const c1 = matchAt(RE_CELL, src, i);
  if (c1) {
    const afterFirst = RE_CELL.lastIndex;
    if (src.charAt(afterFirst) === ':') {
      const c2 = matchAt(RE_CELL, src, afterFirst + 1);
      if (c2) {
        return { type: 'range', value: src.slice(start, RE_CELL.lastIndex), sheet: sheet, sheetQuoted: sheetQuoted, start: start, end: RE_CELL.lastIndex };
      }
    }
    return { type: 'ref', value: src.slice(start, afterFirst), sheet: sheet, sheetQuoted: sheetQuoted, start: start, end: afterFirst };
  }

  // whole-column range: COL ':' COL
  const k1 = matchAt(RE_COL, src, i);
  if (k1) {
    const afterCol = RE_COL.lastIndex;
    if (src.charAt(afterCol) === ':') {
      const k2 = matchAt(RE_COL, src, afterCol + 1);
      if (k2) {
        return { type: 'range', value: src.slice(start, RE_COL.lastIndex), sheet: sheet, sheetQuoted: sheetQuoted, start: start, end: RE_COL.lastIndex };
      }
    }
  }

  // sheet-qualified whole-row range: Sheet1!3:9
  const rr = matchAt(RE_ROWRANGE, src, i);
  if (rr) {
    return { type: 'range', value: src.slice(start, RE_ROWRANGE.lastIndex), sheet: sheet, sheetQuoted: sheetQuoted, start: start, end: RE_ROWRANGE.lastIndex };
  }

  const bool = matchAt(RE_BOOL, src, i);
  if (bool && sheet === null) {
    return { type: 'bool', value: bool[0].toUpperCase() === 'TRUE', start: start, end: RE_BOOL.lastIndex };
  }

  const nm = matchAt(RE_NAME, src, i);
  if (nm) {
    return {
      type: 'name',
      value: nm[0],
      sheet: sheet,
      start: start,
      end: RE_NAME.lastIndex,
    };
  }

  if (sheet !== null) {
    throw new FormulaParseError(
      'sheet prefix at position ' + String(pos) + ' is not followed by a reference',
      { position: pos, formula: whole, reason: 'sheet prefix without a reference' }
    );
  }
  return null;
}

/* ---------------------------------------------------------------------------
 * 4. Parser (precedence climbing; Excel's precedence, including unary minus
 *    binding tighter than '^' so -2^2 is 4 exactly as Excel computes it)
 * ------------------------------------------------------------------------ */

const COMPARE_OPS = ['=', '<>', '<', '>', '<=', '>='];

/**
 * Parse a formula into an AST. A leading '=' (and Excel's '=+' idiom) is
 * accepted. Throws FormulaParseError on a syntax the grammar does not cover —
 * evaluateFormula turns that into `status: 'unsupported'` rather than letting
 * it abort a parse run.
 *
 * Node types: number | string | bool | error | ref | range | name | call |
 * binary | unary | postfix | external | sheet3d | array | missing.
 */
export function parseFormula(text) {
  if (typeof text !== 'string') {
    throw new FormulaEngineError('formula must be a string, received ' + typeof text, {
      code: 'CTA_FORMULA_BAD_INPUT',
      reason: 'formula must be a string',
    });
  }
  let body = text.trim();
  if (body.charAt(0) === '{' && body.charAt(body.length - 1) === '}') {
    // Array-entered (CSE) formula. Its semantics are array semantics, which
    // this engine does not implement; say so instead of evaluating the inside
    // as a scalar and reporting a number Excel never showed (G13).
    return { type: 'array', text: body };
  }
  if (body.charAt(0) === '=') body = body.slice(1);
  body = body.replace(/^[ \t]+/, '');
  if (body.length === 0) {
    throw new FormulaParseError('empty formula', { position: 0, formula: text, reason: 'empty formula' });
  }
  const tokens = tokenizeFormula(body);
  const state = { tokens: tokens, i: 0, formula: text };
  const node = parseCompare(state);
  if (state.i < tokens.length) {
    const t = tokens[state.i];
    throw new FormulaParseError(
      'unexpected ' + t.type + ' ' + JSON.stringify(String(t.value)) + ' at position ' + String(t.start),
      { position: t.start, formula: text, reason: 'unexpected ' + t.type }
    );
  }
  return node;
}

function peek(state) {
  return state.i < state.tokens.length ? state.tokens[state.i] : null;
}

function isOp(tok, values) {
  return tok !== null && tok.type === 'op' && values.indexOf(tok.value) !== -1;
}

function parseCompare(state) {
  let left = parseConcat(state);
  let t = peek(state);
  while (isOp(t, COMPARE_OPS)) {
    state.i += 1;
    const right = parseConcat(state);
    left = { type: 'binary', op: t.value, left: left, right: right };
    t = peek(state);
  }
  return left;
}

function parseConcat(state) {
  let left = parseAdditive(state);
  let t = peek(state);
  while (isOp(t, ['&'])) {
    state.i += 1;
    const right = parseAdditive(state);
    left = { type: 'binary', op: '&', left: left, right: right };
    t = peek(state);
  }
  return left;
}

function parseAdditive(state) {
  let left = parseMultiplicative(state);
  let t = peek(state);
  while (isOp(t, ['+', '-'])) {
    state.i += 1;
    const right = parseMultiplicative(state);
    left = { type: 'binary', op: t.value, left: left, right: right };
    t = peek(state);
  }
  return left;
}

function parseMultiplicative(state) {
  let left = parsePower(state);
  let t = peek(state);
  while (isOp(t, ['*', '/'])) {
    state.i += 1;
    const right = parsePower(state);
    left = { type: 'binary', op: t.value, left: left, right: right };
    t = peek(state);
  }
  return left;
}

function parsePower(state) {
  let left = parseUnary(state);
  let t = peek(state);
  while (isOp(t, ['^'])) {
    state.i += 1;
    const right = parseUnary(state);
    left = { type: 'binary', op: '^', left: left, right: right };
    t = peek(state);
  }
  return left;
}

function parseUnary(state) {
  const t = peek(state);
  if (isOp(t, ['+', '-'])) {
    state.i += 1;
    const operand = parseUnary(state);
    if (t.value === '+') return operand;
    return { type: 'unary', op: '-', operand: operand };
  }
  return parsePostfix(state);
}

function parsePostfix(state) {
  let node = parsePrimary(state);
  let t = peek(state);
  while (isOp(t, ['%'])) {
    state.i += 1;
    node = { type: 'postfix', op: '%', operand: node };
    t = peek(state);
  }
  return node;
}

function parsePrimary(state) {
  const t = peek(state);
  if (t === null) {
    throw new FormulaParseError('formula ends unexpectedly', { position: null, formula: state.formula, reason: 'unexpected end of formula' });
  }
  if (t.type === 'number') { state.i += 1; return { type: 'number', value: t.value }; }
  if (t.type === 'string') { state.i += 1; return { type: 'string', value: t.value }; }
  if (t.type === 'bool') { state.i += 1; return { type: 'bool', value: t.value }; }
  if (t.type === 'error') { state.i += 1; return { type: 'error', value: t.value }; }
  if (t.type === 'external') { state.i += 1; return { type: 'external', text: t.value }; }
  if (t.type === 'sheet3d') { state.i += 1; return { type: 'sheet3d', text: t.value }; }

  if (t.type === 'lparen') {
    state.i += 1;
    const inner = parseCompare(state);
    const close = peek(state);
    if (close === null || close.type !== 'rparen') {
      throw new FormulaParseError('missing closing parenthesis', { position: t.start, formula: state.formula, reason: 'missing closing parenthesis' });
    }
    state.i += 1;
    return inner;
  }

  if (t.type === 'ref') {
    state.i += 1;
    const parsed = parseCellRef(stripSheetPrefix(t.value));
    if (!parsed) {
      throw new FormulaParseError('unreadable cell reference ' + JSON.stringify(t.value), { position: t.start, formula: state.formula, reason: 'unreadable cell reference' });
    }
    return {
      type: 'ref',
      sheet: t.sheet === undefined ? null : t.sheet,
      row: parsed.row,
      col: parsed.col,
      absRow: parsed.absRow,
      absCol: parsed.absCol,
      text: t.value,
    };
  }

  if (t.type === 'range') {
    state.i += 1;
    const parsed = parseRangeRef(stripSheetPrefix(t.value));
    if (!parsed) {
      throw new FormulaParseError('unreadable range ' + JSON.stringify(t.value), { position: t.start, formula: state.formula, reason: 'unreadable range' });
    }
    return {
      type: 'range',
      sheet: t.sheet === undefined ? null : t.sheet,
      r1: parsed.r1,
      c1: parsed.c1,
      r2: parsed.r2,
      c2: parsed.c2,
      text: t.value,
    };
  }

  if (t.type === 'name') {
    state.i += 1;
    const next = peek(state);
    if (next !== null && next.type === 'lparen') {
      state.i += 1;
      const args = parseArgs(state, t);
      return { type: 'call', name: normaliseFunctionName(t.value), rawName: t.value, args: args };
    }
    return { type: 'name', name: t.value, sheet: t.sheet === undefined ? null : t.sheet, text: (t.sheet ? t.sheet + '!' : '') + t.value };
  }

  throw new FormulaParseError(
    'unexpected ' + t.type + ' ' + JSON.stringify(String(t.value)) + ' at position ' + String(t.start),
    { position: t.start, formula: state.formula, reason: 'unexpected ' + t.type }
  );
}

function parseArgs(state, nameTok) {
  const args = [];
  const first = peek(state);
  if (first !== null && first.type === 'rparen') {
    state.i += 1;
    return args;
  }
  for (;;) {
    const t = peek(state);
    if (t !== null && (t.type === 'comma' || t.type === 'rparen')) {
      args.push({ type: 'missing' });
    } else {
      args.push(parseCompare(state));
    }
    const sep = peek(state);
    if (sep === null) {
      throw new FormulaParseError(
        'missing closing parenthesis for ' + nameTok.value,
        { position: nameTok.start, formula: state.formula, reason: 'missing closing parenthesis' }
      );
    }
    if (sep.type === 'comma') { state.i += 1; continue; }
    if (sep.type === 'rparen') { state.i += 1; break; }
    throw new FormulaParseError(
      'unexpected ' + sep.type + ' in arguments of ' + nameTok.value,
      { position: sep.start, formula: state.formula, reason: 'unexpected token in argument list' }
    );
  }
  return args;
}

function stripSheetPrefix(text) {
  const split = findSheetSplit(text);
  return split === null ? text : split.rest;
}

/**
 * 'sum' -> 'SUM'; Excel's '_xlfn.' marker for post-2007 functions is stripped
 * so the inventory records IFS, not _xlfn.IFS.
 */
function normaliseFunctionName(raw) {
  let name = String(raw).toUpperCase();
  if (name.indexOf('_XLFN.') === 0) name = name.slice(6);
  if (name.indexOf('_XLWS.') === 0) name = name.slice(6);
  return name;
}

/* ---------------------------------------------------------------------------
 * 5. AST walking — what recompute.js reads to find totals and short SUM ranges
 * ------------------------------------------------------------------------ */

/** Depth-first walk. `visit(node)` may return false to skip a node's children. */
export function walkFormulaAst(node, visit) {
  if (!node || typeof node !== 'object') return;
  const go = visit(node);
  if (go === false) return;
  if (node.type === 'call') {
    for (let i = 0; i < node.args.length; i += 1) walkFormulaAst(node.args[i], visit);
  } else if (node.type === 'binary') {
    walkFormulaAst(node.left, visit);
    walkFormulaAst(node.right, visit);
  } else if (node.type === 'unary' || node.type === 'postfix') {
    walkFormulaAst(node.operand, visit);
  }
}

/**
 * Every reference a formula mentions, with `defaultSheet` filled in where the
 * formula did not name one.
 * Returns { refs: [{ sheet, row, col }], ranges: [{ sheet, r1, c1, r2, c2 }] }.
 */
export function collectReferences(ast, defaultSheet) {
  const refs = [];
  const ranges = [];
  walkFormulaAst(ast, function (node) {
    if (node.type === 'ref') {
      refs.push({ sheet: node.sheet === null ? defaultSheet : node.sheet, row: node.row, col: node.col });
    } else if (node.type === 'range') {
      ranges.push({
        sheet: node.sheet === null ? defaultSheet : node.sheet,
        r1: node.r1,
        c1: node.c1,
        r2: node.r2,
        c2: node.c2,
      });
    }
    return true;
  });
  return { refs: refs, ranges: ranges };
}

/**
 * Everything in a formula the engine cannot evaluate, ALL of it, before any
 * evaluation runs. Reporting the whole list (rather than throwing on the
 * first) is what plan §6.3 means by "the function name recorded in the
 * inventory": a cell using two unlisted functions names both.
 *
 * Note the deliberate conservatism: `IF(FALSE, NOTAFUNCTION(), 1)` is reported
 * unsupported even though Excel would never evaluate the dead branch. The
 * engine refuses to claim a number for a formula it cannot fully read (G13).
 */
export function scanUnsupported(ast, definedNames) {
  const seen = {};
  const out = [];
  function add(kind, name, detail) {
    const key = kind + '::' + String(name);
    if (seen[key]) return;
    seen[key] = true;
    out.push({ kind: kind, name: name, detail: detail === undefined ? null : detail });
  }
  walkFormulaAst(ast, function (node) {
    if (node.type === 'call' && SUPPORTED_FUNCTIONS.indexOf(node.name) === -1) {
      add('function', node.name, 'function not in the plan §6.3 set');
    } else if (node.type === 'external') {
      add('external_reference', node.text, 'reference into another workbook');
    } else if (node.type === 'sheet3d') {
      add('3d_reference', node.text, 'reference spanning several sheets');
    } else if (node.type === 'array') {
      add('array_formula', node.text, 'array-entered (CSE) formula');
    } else if (node.type === 'name') {
      if (!definedNames || !hasOwn(definedNames, node.name.toUpperCase())) {
        add('defined_name', node.text || node.name, 'defined name not supplied to the engine');
      }
    }
    return true;
  });
  return out;
}

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

/* ---------------------------------------------------------------------------
 * 6. The §5.5 cell shape
 * ------------------------------------------------------------------------ */

/**
 * True when a §5.5 `cta_cells` row carries a formula. Shared formulas are
 * expanded by the reader (WP-22, §6.3 "expand the master cell's formula for
 * every cell in the group by shifting references"), so by the time a cell
 * reaches this engine `formula` is that cell's OWN formula text.
 */
export function cellHasFormula(cell) {
  return !!cell && typeof cell.formula === 'string' && cell.formula.trim().length > 0;
}

/**
 * The value a §5.5 cell holds on its own, ignoring any formula: the number for
 * a numeric cell, the text for a string cell, the boolean, the error box, or
 * null for blank. Used for input cells, and as the "cached" side of the
 * recompute-and-diff (G7).
 */
export function cellStoredValue(cell) {
  if (!cell) return null;
  const t = cell.value_type;
  if (t === 'n') {
    if (typeof cell.number_value === 'number') return cell.number_value;
    const n = toFiniteNumber(cell.number_value);
    if (n !== null) return n;
    const fromText = toFiniteNumber(cell.raw_text);
    return fromText === null ? null : fromText;
  }
  if (t === 's') {
    if (typeof cell.raw_text === 'string') return cell.raw_text;
    if (typeof cell.cached_text === 'string') return cell.cached_text;
    return null;
  }
  if (t === 'b') {
    if (typeof cell.number_value === 'number') return cell.number_value !== 0;
    const raw = typeof cell.raw_text === 'string' ? cell.raw_text.trim().toUpperCase() : '';
    if (raw === 'TRUE' || raw === '1') return true;
    if (raw === 'FALSE' || raw === '0') return false;
    return null;
  }
  if (t === 'e') {
    const raw = typeof cell.raw_text === 'string' ? cell.raw_text.trim() : (typeof cell.cached_text === 'string' ? cell.cached_text.trim() : '');
    return isErrorValue(raw) ? makeError(raw) : makeError('#VALUE!');
  }
  if (t === 'blank') return null;
  if (t === 'd') {
    // Date cells are stored as their serial (§6.3: "date serials decoded when
    // the number format is a date"); arithmetic uses the serial, as Excel does.
    const n = toFiniteNumber(cell.number_value);
    return n === null ? null : n;
  }
  // value_type absent: infer, and never invent.
  const n = toFiniteNumber(cell.number_value);
  if (n !== null) return n;
  if (typeof cell.raw_text === 'string' && cell.raw_text.length > 0) {
    return isErrorValue(cell.raw_text.trim()) ? makeError(cell.raw_text.trim()) : cell.raw_text;
  }
  if (typeof cell.cached_text === 'string' && cell.cached_text.length > 0) {
    return isErrorValue(cell.cached_text.trim()) ? makeError(cell.cached_text.trim()) : cell.cached_text;
  }
  return null;
}

/**
 * The value Excel last SAVED for a formula cell — the cached value §6.3 keeps
 * beside the formula. `undefined` means the workbook carries no cached value
 * for that cell (distinct from `null`, which is a cached blank).
 */
export function cellCachedValue(cell) {
  if (!cell) return undefined;
  const n = toFiniteNumber(cell.number_value);
  if (n !== null) return n;
  const text = typeof cell.cached_text === 'string' ? cell.cached_text
    : (typeof cell.raw_text === 'string' ? cell.raw_text : null);
  if (text === null) return undefined;
  const trimmed = text.trim();
  if (isErrorValue(trimmed)) return makeError(trimmed);
  if (cell.value_type === 'b') {
    if (trimmed.toUpperCase() === 'TRUE') return true;
    if (trimmed.toUpperCase() === 'FALSE') return false;
  }
  if (text.length === 0) return undefined;
  return text;
}

function toFiniteNumber(v) {
  if (typeof v === 'number') return isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const t = v.trim();
    if (t.length === 0) return null;
    if (!/^[+-]?(?:[0-9]+(?:\.[0-9]*)?|\.[0-9]+)(?:[eE][+-]?[0-9]+)?$/.test(t)) return null;
    const n = parseFloat(t);
    return isFinite(n) ? n : null;
  }
  return null;
}

/* ---------------------------------------------------------------------------
 * 7. Workbook context
 * ------------------------------------------------------------------------ */

function sheetKey(name) {
  return String(name).toUpperCase();
}

function cellKey(row, col) {
  return String(row) + ':' + String(col);
}

/**
 * Build an evaluation context over a workbook described as §5.5 rows.
 *
 * `sheets` is an array of { name, cells } (or a plain object mapping a sheet
 * name to its cells array). Each cell needs `row` and `col` (1-based) or a
 * parseable `ref`; everything else is the §5.5 column set.
 *
 * options:
 *   formulaInputs   'recompute' (default) — a formula cell's value, as seen by
 *                   the cells that reference it, is its RECOMPUTED value, so a
 *                   total is rebuilt from its line items all the way down
 *                   (§6.3 "every total row is recomputed from its line items").
 *                   'cached' — use the workbook's saved values instead, which
 *                   is what a caller wants to reproduce the printed workbook.
 *   definedNames    { NAME: 'Sheet1!$B$3' } — §8.7's named ranges. A name not
 *                   in the map is reported unsupported, never guessed.
 *   maxRangeCells   guard for whole-column ranges (default 1,000,000).
 *
 * Returned object:
 *   sheetNames                       string[] in the order given
 *   getCell(sheet, row, col)         the §5.5 row, or null
 *   getCellByRef(sheet, 'C90')       the §5.5 row, or null
 *   getExtent(sheet)                 { minRow, maxRow, minCol, maxCol } | null
 *   getValue(sheet, row, col, note)  the evaluated value (see formulaInputs)
 *   contextForSheet(sheet)           a context to hand evaluateFormula
 *   evaluateCell(sheet, 'C90')       memoised result for one formula cell
 *   results                          Map 'SHEET!C90' -> result
 */
export function createWorkbookContext(sheets, options) {
  const opts = options || {};
  const formulaInputs = opts.formulaInputs === 'cached' ? 'cached' : 'recompute';
  const maxRangeCells = typeof opts.maxRangeCells === 'number' ? opts.maxRangeCells : 1000000;
  const definedNames = normaliseDefinedNames(opts.definedNames);

  const list = normaliseSheetList(sheets);
  const bySheet = {};
  const order = [];
  for (let s = 0; s < list.length; s += 1) {
    const entry = list[s];
    const key = sheetKey(entry.name);
    if (hasOwn(bySheet, key)) {
      throw new FormulaEngineError('duplicate sheet name ' + JSON.stringify(entry.name), {
        code: 'CTA_FORMULA_DUPLICATE_SHEET',
        location: entry.name,
        reason: 'two sheets share a name (case-insensitively)',
      });
    }
    const index = { name: entry.name, cells: entry.cells, byKey: {}, extent: null };
    let minRow = null;
    let maxRow = null;
    let minCol = null;
    let maxCol = null;
    for (let i = 0; i < entry.cells.length; i += 1) {
      const cell = entry.cells[i];
      const pos = cellPosition(cell, entry.name, i);
      const k = cellKey(pos.row, pos.col);
      index.byKey[k] = cell;
      if (minRow === null || pos.row < minRow) minRow = pos.row;
      if (maxRow === null || pos.row > maxRow) maxRow = pos.row;
      if (minCol === null || pos.col < minCol) minCol = pos.col;
      if (maxCol === null || pos.col > maxCol) maxCol = pos.col;
    }
    if (minRow !== null) index.extent = { minRow: minRow, maxRow: maxRow, minCol: minCol, maxCol: maxCol };
    bySheet[key] = index;
    order.push(entry.name);
  }

  const results = new Map();
  const inProgress = {};

  function resolveSheet(name) {
    if (name === null || name === undefined) return null;
    const direct = bySheet[sheetKey(name)];
    if (direct) return direct;
    // Trailing-space sheet names are real in this corpus (Oramed "Per Subject ",
    // A.7): match on the trimmed name before giving up, and never invent a sheet.
    const trimmed = sheetKey(String(name).trim());
    for (let i = 0; i < order.length; i += 1) {
      if (sheetKey(String(order[i]).trim()) === trimmed) return bySheet[sheetKey(order[i])];
    }
    return null;
  }

  function getCell(sheetName, row, col) {
    const sheet = resolveSheet(sheetName);
    if (!sheet) return null;
    const cell = sheet.byKey[cellKey(row, col)];
    return cell === undefined ? null : cell;
  }

  function getCellByRef(sheetName, ref) {
    const parsed = parseCellRef(ref);
    if (!parsed) return null;
    return getCell(parsed.sheet === null ? sheetName : parsed.sheet, parsed.row, parsed.col);
  }

  function getExtent(sheetName) {
    const sheet = resolveSheet(sheetName);
    return sheet ? sheet.extent : null;
  }

  function getValue(sheetName, row, col, note) {
    const sheet = resolveSheet(sheetName);
    if (!sheet) {
      raiseUnsupported('unknown_sheet', String(sheetName), 'the workbook has no such sheet');
    }
    const cell = sheet.byKey[cellKey(row, col)];
    if (cell === undefined) return null;
    if (!cellHasFormula(cell) || formulaInputs === 'cached') {
      return cellHasFormula(cell) ? normaliseCached(cellCachedValue(cell)) : cellStoredValue(cell);
    }
    const ref = sheet.name + '!' + formatCellRef(row, col);
    const res = evaluateCell(sheet.name, formatCellRef(row, col));
    if (res.status === 'ok') return res.value;
    if (res.status === 'error') {
      // A dependency that is part of a reference cycle makes THIS cell part of
      // it too: re-raise so every cell in the loop reports "circular
      // reference" naming the loop, instead of the first one saying so and the
      // rest reporting a bare #VALUE! nobody can trace (G13).
      if (res.cycle) throw new CycleSignal(res.cycle);
      return makeError(res.errorValue === null ? '#VALUE!' : res.errorValue);
    }
    // Unsupported dependency: plan §6.3 — "the cached value is used and
    // labelled extracted; the cell is never guessed". Record the substitution
    // so the dependent cell's provenance says a cached input was used (G7/G8).
    const cached = cellCachedValue(cell);
    if (cached === undefined) {
      raiseUnsupported('dependency', ref, 'depends on an unsupported cell that carries no cached value');
    }
    if (typeof note === 'function') note('used_cached_input', { ref: ref, reason: res.reason });
    return normaliseCached(cached);
  }

  function contextForSheet(sheetName) {
    return {
      sheet: sheetName,
      getValue: getValue,
      getExtent: getExtent,
      definedNames: definedNames,
      maxRangeCells: maxRangeCells,
    };
  }

  function evaluateCell(sheetName, ref) {
    const sheet = resolveSheet(sheetName);
    if (!sheet) {
      throw new FormulaEngineError('no sheet named ' + JSON.stringify(String(sheetName)), {
        code: 'CTA_FORMULA_UNKNOWN_SHEET',
        location: String(sheetName),
        reason: 'no such sheet in this workbook',
      });
    }
    const parsed = parseCellRef(ref);
    if (!parsed) {
      throw new FormulaEngineError('not a cell reference: ' + JSON.stringify(String(ref)), {
        code: 'CTA_FORMULA_BAD_REF',
        location: String(ref),
        reason: 'not an A1 cell reference',
      });
    }
    const canonical = sheet.name + '!' + formatCellRef(parsed.row, parsed.col);
    const memo = results.get(canonical);
    if (memo) return memo;
    const cell = sheet.byKey[cellKey(parsed.row, parsed.col)];
    if (cell === undefined || !cellHasFormula(cell)) {
      const notFormula = makeResult('not_formula', null, [], [], null, 'cell carries no formula');
      results.set(canonical, notFormula);
      return notFormula;
    }
    if (inProgress[canonical]) {
      throw new CycleSignal([canonical]);
    }
    inProgress[canonical] = true;
    let out;
    try {
      out = evaluateFormula(cell.formula, contextForSheet(sheet.name));
    } catch (e) {
      if (e instanceof CycleSignal) {
        const path = [canonical].concat(e.path);
        out = makeResult('error', null, [], [], null, 'circular reference: ' + path.join(' -> '));
        out.cycle = path;
      } else {
        throw e;
      }
    } finally {
      delete inProgress[canonical];
    }
    out.ref = canonical;
    results.set(canonical, out);
    return out;
  }

  return {
    sheetNames: order,
    formulaInputs: formulaInputs,
    definedNames: definedNames,
    getCell: getCell,
    getCellByRef: getCellByRef,
    getExtent: getExtent,
    getValue: getValue,
    contextForSheet: contextForSheet,
    evaluateCell: evaluateCell,
    results: results,
  };
}

function normaliseCached(v) {
  return v === undefined ? null : v;
}

function normaliseSheetList(sheets) {
  const out = [];
  if (Array.isArray(sheets)) {
    for (let i = 0; i < sheets.length; i += 1) {
      const s = sheets[i];
      if (!s || typeof s !== 'object' || typeof s.name !== 'string' || !Array.isArray(s.cells)) {
        throw new FormulaEngineError('sheet ' + String(i) + ' must carry a name and a cells array', {
          code: 'CTA_FORMULA_BAD_WORKBOOK',
          location: 'sheets[' + String(i) + ']',
          reason: 'sheet must be { name, cells }',
        });
      }
      out.push({ name: s.name, cells: s.cells });
    }
    return out;
  }
  if (sheets && typeof sheets === 'object') {
    const keys = Object.keys(sheets);
    for (let i = 0; i < keys.length; i += 1) {
      const cells = sheets[keys[i]];
      if (!Array.isArray(cells)) {
        throw new FormulaEngineError('sheet ' + JSON.stringify(keys[i]) + ' must map to an array of cells', {
          code: 'CTA_FORMULA_BAD_WORKBOOK',
          location: keys[i],
          reason: 'sheet must map to an array of cells',
        });
      }
      out.push({ name: keys[i], cells: cells });
    }
    return out;
  }
  throw new FormulaEngineError('createWorkbookContext needs an array of { name, cells } or an object of name -> cells', {
    code: 'CTA_FORMULA_BAD_WORKBOOK',
    reason: 'workbook description is not an array or object',
  });
}

function cellPosition(cell, sheetName, i) {
  if (!cell || typeof cell !== 'object') {
    throw new FormulaEngineError('cell ' + String(i) + ' of sheet ' + JSON.stringify(sheetName) + ' is not an object', {
      code: 'CTA_FORMULA_BAD_CELL',
      location: sheetName + ' cell index ' + String(i),
      reason: 'cell is not an object',
    });
  }
  const row = typeof cell.row === 'number' ? cell.row : null;
  const col = typeof cell.col === 'number' ? cell.col : null;
  if (row !== null && col !== null) return { row: row, col: col };
  const parsed = parseCellRef(cell.ref);
  if (!parsed) {
    throw new FormulaEngineError(
      'cell ' + String(i) + ' of sheet ' + JSON.stringify(sheetName) + ' has neither row/col nor a readable ref',
      {
        code: 'CTA_FORMULA_BAD_CELL',
        location: sheetName + ' cell index ' + String(i),
        reason: 'cell needs row and col, or a parseable A1 ref',
      }
    );
  }
  return { row: parsed.row, col: parsed.col };
}

function normaliseDefinedNames(input) {
  if (!input) return null;
  const out = {};
  const keys = Object.keys(input);
  for (let i = 0; i < keys.length; i += 1) out[keys[i].toUpperCase()] = input[keys[i]];
  return keys.length === 0 ? null : out;
}

/* ---------------------------------------------------------------------------
 * 8. Evaluation
 * ------------------------------------------------------------------------ */

function makeResult(status, value, unsupported, usedCachedInputs, errorValue, reason) {
  let valueType = null;
  let numberValue = null;
  if (status === 'ok') {
    if (typeof value === 'number') { valueType = 'n'; numberValue = value; }
    else if (typeof value === 'string') valueType = 's';
    else if (typeof value === 'boolean') valueType = 'b';
    else valueType = 'blank';
  } else if (status === 'error' && errorValue !== null) {
    valueType = 'e';
  }
  const names = [];
  for (let i = 0; i < unsupported.length; i += 1) {
    if (unsupported[i].kind === 'function') names.push(unsupported[i].name);
  }
  return {
    status: status,
    valueType: valueType,
    value: status === 'ok' ? value : null,
    numberValue: numberValue,
    errorValue: errorValue === undefined ? null : errorValue,
    unsupported: unsupported,
    unsupportedFunctions: names,
    usedCachedInputs: usedCachedInputs,
    reason: reason === undefined ? null : reason,
    engineVersion: FORMULA_ENGINE_VERSION,
  };
}

/**
 * Evaluate a formula string.
 *
 * `context` (all optional except when the formula needs them):
 *   sheet          name of the sheet the formula lives on
 *   getValue(sheet, row, col, note) -> value   (see the value model above)
 *   getExtent(sheet) -> { minRow, maxRow, minCol, maxCol } | null
 *   definedNames   { NAME: 'Sheet1!$B$3' }
 *   maxRangeCells  number
 *
 * Returns (this is the documented contract; extra fields may be added but
 * these never change shape):
 *   {
 *     status: 'ok' | 'unsupported' | 'error',
 *     valueType: 'n' | 's' | 'b' | 'e' | 'blank' | null,
 *     value, numberValue, errorValue,
 *     unsupported: [{ kind, name, detail }],
 *     unsupportedFunctions: [name],
 *     usedCachedInputs: [{ ref, reason }],
 *     reason, engineVersion
 *   }
 */
export function evaluateFormula(text, context) {
  let ast;
  try {
    ast = parseFormula(text);
  } catch (e) {
    if (e instanceof FormulaParseError) {
      return makeResult('unsupported', null, [{ kind: 'syntax', name: null, detail: e.reason }], [], null, e.message);
    }
    throw e;
  }
  return evaluateParsed(ast, context);
}

/** evaluateFormula for an AST already produced by parseFormula. */
export function evaluateParsed(ast, context) {
  const ctx = {
    sheet: context && context.sheet !== undefined ? context.sheet : null,
    getValue: context && typeof context.getValue === 'function' ? context.getValue : null,
    getExtent: context && typeof context.getExtent === 'function' ? context.getExtent : null,
    definedNames: context && context.definedNames ? normaliseDefinedNames(context.definedNames) : null,
    maxRangeCells: context && typeof context.maxRangeCells === 'number' ? context.maxRangeCells : 1000000,
  };
  const usedCachedInputs = [];
  ctx.note = function (kind, payload) {
    if (kind === 'used_cached_input') usedCachedInputs.push(payload);
  };

  const unsupported = scanUnsupported(ast, ctx.definedNames);
  if (unsupported.length > 0) {
    return makeResult('unsupported', null, unsupported, usedCachedInputs, null, describeUnsupported(unsupported));
  }

  try {
    const v = evalNode(ast, ctx);
    const scalar = deRange(v, ctx);
    if (isErrorBox(scalar)) {
      return makeResult('error', null, [], usedCachedInputs, scalar.__error, 'formula evaluates to ' + scalar.__error);
    }
    return makeResult('ok', scalar, [], usedCachedInputs, null, null);
  } catch (e) {
    if (e instanceof ErrorSignal) {
      return makeResult('error', null, [], usedCachedInputs, e.errorValue, 'formula evaluates to ' + e.errorValue);
    }
    if (e instanceof UnsupportedSignal) {
      return makeResult('unsupported', null, [e.item], usedCachedInputs, null, describeUnsupported([e.item]));
    }
    throw e;
  }
}

function describeUnsupported(items) {
  const parts = [];
  for (let i = 0; i < items.length; i += 1) {
    const it = items[i];
    parts.push(it.kind + (it.name ? ' ' + String(it.name) : ''));
  }
  return 'unsupported: ' + parts.join(', ');
}

function evalNode(node, ctx) {
  switch (node.type) {
    case 'number': return node.value;
    case 'string': return node.value;
    case 'bool': return node.value;
    case 'error': return makeError(node.value);
    case 'missing': return null;
    case 'ref': return readRef(node, ctx);
    case 'range': return { __range: resolveRange(node, ctx) };
    case 'name': return evalDefinedName(node, ctx);
    case 'unary': {
      const v = toNumber(deRange(evalNode(node.operand, ctx), ctx));
      return -v;
    }
    case 'postfix': {
      const v = toNumber(deRange(evalNode(node.operand, ctx), ctx));
      return v / 100;
    }
    case 'binary': return evalBinary(node, ctx);
    case 'call': return callFunction(node, ctx);
    case 'external': raiseUnsupported('external_reference', node.text, 'reference into another workbook'); return null;
    case 'sheet3d': raiseUnsupported('3d_reference', node.text, 'reference spanning several sheets'); return null;
    case 'array': raiseUnsupported('array_formula', node.text, 'array-entered (CSE) formula'); return null;
    default:
      raiseUnsupported('syntax', node.type, 'unknown node type');
      return null;
  }
}

function evalDefinedName(node, ctx) {
  const key = node.name.toUpperCase();
  if (!ctx.definedNames || !hasOwn(ctx.definedNames, key)) {
    raiseUnsupported('defined_name', node.text || node.name, 'defined name not supplied to the engine');
  }
  const target = ctx.definedNames[key];
  if (typeof target !== 'string') {
    raiseUnsupported('defined_name', node.text || node.name, 'defined name does not resolve to a reference');
  }
  const asRange = parseRangeRef(target);
  if (asRange) {
    return { __range: resolveRange({ sheet: asRange.sheet, r1: asRange.r1, c1: asRange.c1, r2: asRange.r2, c2: asRange.c2 }, ctx) };
  }
  const asCell = parseCellRef(target);
  if (asCell) {
    return readRef({ sheet: asCell.sheet, row: asCell.row, col: asCell.col }, ctx);
  }
  raiseUnsupported('defined_name', node.text || node.name, 'defined name resolves to ' + JSON.stringify(target) + ', which is not a reference');
  return null;
}

function readRef(node, ctx) {
  if (!ctx.getValue) {
    raiseUnsupported('unresolved_reference', refText(node, ctx), 'the evaluation context supplies no cell values');
  }
  const sheet = node.sheet === null || node.sheet === undefined ? ctx.sheet : node.sheet;
  return ctx.getValue(sheet, node.row, node.col, ctx.note);
}

function refText(node, ctx) {
  const sheet = node.sheet === null || node.sheet === undefined ? ctx.sheet : node.sheet;
  const cell = formatCellRef(node.row, node.col);
  return sheet ? String(sheet) + '!' + cell : cell;
}

function resolveRange(node, ctx) {
  const sheet = node.sheet === null || node.sheet === undefined ? ctx.sheet : node.sheet;
  let r1 = node.r1;
  let r2 = node.r2;
  let c1 = node.c1;
  let c2 = node.c2;
  if (r1 === null || c1 === null) {
    const extent = ctx.getExtent ? ctx.getExtent(sheet) : null;
    if (!extent) {
      raiseUnsupported(
        'open_range',
        node.text || 'range',
        'whole-column or whole-row reference with no sheet extent to resolve it against'
      );
    }
    if (r1 === null) { r1 = extent.minRow; r2 = extent.maxRow; }
    if (c1 === null) { c1 = extent.minCol; c2 = extent.maxCol; }
  }
  const height = r2 - r1 + 1;
  const width = c2 - c1 + 1;
  if (height * width > ctx.maxRangeCells) {
    raiseUnsupported('open_range', node.text || 'range', 'range covers ' + String(height * width) + ' cells');
  }
  return { sheet: sheet, r1: r1, c1: c1, r2: r2, c2: c2 };
}

function readCell(ctx, sheet, row, col) {
  if (!ctx.getValue) {
    raiseUnsupported(
      'unresolved_reference',
      (sheet ? String(sheet) + '!' : '') + formatCellRef(row, col),
      'the evaluation context supplies no cell values'
    );
  }
  return ctx.getValue(sheet, row, col, ctx.note);
}

/** A 1x1 range behaves as its single value; anything larger is #VALUE!. */
function deRange(v, ctx) {
  if (!isRangeBox(v)) return v;
  const r = v.__range;
  if (r.r1 === r.r2 && r.c1 === r.c2) return readCell(ctx, r.sheet, r.r1, r.c1);
  raiseError('#VALUE!');
  return null;
}

function eachRangeValue(range, ctx, visit) {
  for (let r = range.r1; r <= range.r2; r += 1) {
    for (let c = range.c1; c <= range.c2; c += 1) {
      visit(readCell(ctx, range.sheet, r, c), r, c);
    }
  }
}

/* ---- coercions --------------------------------------------------------- */

const RE_NUMERIC_TEXT = /^[+-]?\$?\s?(?:[0-9]{1,3}(?:,[0-9]{3})+|[0-9]+)(?:\.[0-9]+)?%?$/;

function toNumber(v) {
  if (isErrorBox(v)) throw new ErrorSignal(v.__error);
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (typeof v === 'string') {
    const t = v.trim();
    if (t.length === 0) return 0;
    if (!RE_NUMERIC_TEXT.test(t)) raiseError('#VALUE!');
    const isPct = t.charAt(t.length - 1) === '%';
    const cleaned = t.replace(/[$,%\s]/g, '');
    const n = parseFloat(cleaned);
    if (!isFinite(n)) raiseError('#VALUE!');
    return isPct ? n / 100 : n;
  }
  raiseError('#VALUE!');
  return 0;
}

function toBoolean(v) {
  if (isErrorBox(v)) throw new ErrorSignal(v.__error);
  if (v === null || v === undefined) return false;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') {
    const t = v.trim().toUpperCase();
    if (t === 'TRUE') return true;
    if (t === 'FALSE') return false;
  }
  raiseError('#VALUE!');
  return false;
}

function toText(v) {
  if (isErrorBox(v)) throw new ErrorSignal(v.__error);
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE';
  if (typeof v === 'number') return formatNumberForText(v);
  return String(v);
}

function formatNumberForText(n) {
  // Excel's "&" uses the general number format; String() matches it for the
  // integers and plain decimals this module ever concatenates. Currency and
  // date formatting belong to the display layer (numfmt, §5.5), not here.
  return String(n);
}

/* ---- operators --------------------------------------------------------- */

function evalBinary(node, ctx) {
  const rawL = evalNode(node.left, ctx);
  const rawR = evalNode(node.right, ctx);
  if (isRangeBox(rawL) && isRangeBox(rawR)) {
    // Range OP range is array arithmetic; not implemented, and never guessed.
    raiseUnsupported('array_arithmetic', node.op, 'arithmetic between two ranges');
  }
  const l = deRange(rawL, ctx);
  const r = deRange(rawR, ctx);
  if (isErrorBox(l)) throw new ErrorSignal(l.__error);
  if (isErrorBox(r)) throw new ErrorSignal(r.__error);

  switch (node.op) {
    case '+': return toNumber(l) + toNumber(r);
    case '-': return toNumber(l) - toNumber(r);
    case '*': return toNumber(l) * toNumber(r);
    case '/': {
      const d = toNumber(r);
      if (d === 0) raiseError('#DIV/0!');
      return toNumber(l) / d;
    }
    case '^': {
      const base = toNumber(l);
      const exp = toNumber(r);
      const out = Math.pow(base, exp);
      if (!isFinite(out) || isNaN(out)) raiseError('#NUM!');
      return out;
    }
    case '&': return toText(l) + toText(r);
    default: return compareValues(node.op, l, r);
  }
}

function compareValues(op, l, r) {
  let a = l;
  let b = r;
  // Blank compares as 0 against a number and as "" against text (Excel's rule).
  if (a === null) a = typeof b === 'string' ? '' : (typeof b === 'boolean' ? false : 0);
  if (b === null) b = typeof a === 'string' ? '' : (typeof a === 'boolean' ? false : 0);

  let cmp;
  if (typeof a === 'string' && typeof b === 'string') {
    const ua = a.toUpperCase();
    const ub = b.toUpperCase();
    cmp = ua < ub ? -1 : (ua > ub ? 1 : 0);
  } else if (typeof a === typeof b) {
    const na = typeof a === 'boolean' ? (a ? 1 : 0) : a;
    const nb = typeof b === 'boolean' ? (b ? 1 : 0) : b;
    cmp = na < nb ? -1 : (na > nb ? 1 : 0);
  } else {
    // Mixed types: Excel's order is number < text < FALSE < TRUE.
    cmp = typeRank(a) < typeRank(b) ? -1 : 1;
  }
  switch (op) {
    case '=': return cmp === 0;
    case '<>': return cmp !== 0;
    case '<': return cmp < 0;
    case '<=': return cmp <= 0;
    case '>': return cmp > 0;
    case '>=': return cmp >= 0;
    default:
      raiseUnsupported('syntax', op, 'unknown comparison operator');
      return false;
  }
}

function typeRank(v) {
  if (typeof v === 'number') return 0;
  if (typeof v === 'string') return 1;
  return 2;
}

/* ---- functions --------------------------------------------------------- */

/** Round half AWAY from zero, the way Excel's ROUND does, with binary-drift repair. */
function roundHalfAway(n, digits) {
  if (!isFinite(n)) return n;
  const d = Math.max(-15, Math.min(15, Math.trunc(digits)));
  const f = Math.pow(10, d);
  const scaled = Number((n * f).toPrecision(15));
  const r = scaled < 0 ? -Math.round(-scaled) : Math.round(scaled);
  return r / f;
}

function roundAwayCeil(n, digits) {
  const d = Math.max(-15, Math.min(15, Math.trunc(digits)));
  const f = Math.pow(10, d);
  const scaled = Number((n * f).toPrecision(15));
  const r = scaled < 0 ? -Math.ceil(-scaled) : Math.ceil(scaled);
  return r / f;
}

function roundTowardZero(n, digits) {
  const d = Math.max(-15, Math.min(15, Math.trunc(digits)));
  const f = Math.pow(10, d);
  const scaled = Number((n * f).toPrecision(15));
  const r = scaled < 0 ? -Math.floor(-scaled) : Math.floor(scaled);
  return r / f;
}

/** Numbers a SUM-family function sees: range cells contribute only numbers. */
function numbersOf(v, ctx, out) {
  if (isRangeBox(v)) {
    eachRangeValue(v.__range, ctx, function (cell) {
      if (isErrorBox(cell)) throw new ErrorSignal(cell.__error);
      if (typeof cell === 'number') out.push(cell);
    });
    return out;
  }
  if (isErrorBox(v)) throw new ErrorSignal(v.__error);
  if (v === null || v === undefined) return out;
  out.push(toNumber(v));
  return out;
}

function flatValues(v, ctx, out) {
  if (isRangeBox(v)) {
    eachRangeValue(v.__range, ctx, function (cell) { out.push(cell); });
    return out;
  }
  out.push(v);
  return out;
}

function requireArgs(name, args, min, max) {
  if (args.length < min || (max !== null && args.length > max)) {
    raiseUnsupported(
      'argument_count',
      name,
      name + ' takes ' + String(min) + (max === null ? ' or more' : (max === min ? '' : ' to ' + String(max))) + ' arguments, received ' + String(args.length)
    );
  }
}

function callFunction(node, ctx) {
  const name = node.name;
  const args = node.args;

  // Lazy functions first: their whole point is NOT evaluating every branch.
  if (name === 'IF') {
    requireArgs('IF', args, 2, 3);
    const cond = toBoolean(deRange(evalNode(args[0], ctx), ctx));
    if (cond) return deRange(evalNode(args[1], ctx), ctx);
    if (args.length < 3 || args[2].type === 'missing') return false;
    return deRange(evalNode(args[2], ctx), ctx);
  }
  if (name === 'IFERROR') {
    requireArgs('IFERROR', args, 2, 2);
    try {
      const v = deRange(evalNode(args[0], ctx), ctx);
      if (isErrorBox(v)) return deRange(evalNode(args[1], ctx), ctx);
      return v;
    } catch (e) {
      // IFERROR swallows Excel errors ONLY. An unsupported construct inside it
      // stays unsupported: masking it would report a number for a formula the
      // engine never actually read (G13).
      if (e instanceof ErrorSignal) return deRange(evalNode(args[1], ctx), ctx);
      throw e;
    }
  }

  const values = [];
  for (let i = 0; i < args.length; i += 1) values.push(evalNode(args[i], ctx));

  switch (name) {
    case 'SUM': {
      requireArgs('SUM', args, 1, null);
      const nums = [];
      for (let i = 0; i < values.length; i += 1) numbersOf(values[i], ctx, nums);
      let total = 0;
      for (let i = 0; i < nums.length; i += 1) total += nums[i];
      return total;
    }
    case 'PRODUCT': {
      requireArgs('PRODUCT', args, 1, null);
      const nums = [];
      for (let i = 0; i < values.length; i += 1) numbersOf(values[i], ctx, nums);
      if (nums.length === 0) return 0;
      let p = 1;
      for (let i = 0; i < nums.length; i += 1) p *= nums[i];
      return p;
    }
    case 'MIN':
    case 'MAX': {
      requireArgs(name, args, 1, null);
      const nums = [];
      for (let i = 0; i < values.length; i += 1) numbersOf(values[i], ctx, nums);
      if (nums.length === 0) return 0;
      let acc = nums[0];
      for (let i = 1; i < nums.length; i += 1) {
        if (name === 'MIN' ? nums[i] < acc : nums[i] > acc) acc = nums[i];
      }
      return acc;
    }
    case 'AVERAGE': {
      requireArgs('AVERAGE', args, 1, null);
      const nums = [];
      for (let i = 0; i < values.length; i += 1) numbersOf(values[i], ctx, nums);
      if (nums.length === 0) raiseError('#DIV/0!');
      let t = 0;
      for (let i = 0; i < nums.length; i += 1) t += nums[i];
      return t / nums.length;
    }
    case 'COUNT': {
      requireArgs('COUNT', args, 1, null);
      let n = 0;
      for (let i = 0; i < values.length; i += 1) {
        const flat = flatValues(values[i], ctx, []);
        for (let j = 0; j < flat.length; j += 1) {
          const v = flat[j];
          if (typeof v === 'number') n += 1;
          else if (!isRangeBox(values[i]) && typeof v === 'string' && RE_NUMERIC_TEXT.test(v.trim())) n += 1;
        }
      }
      return n;
    }
    case 'COUNTA': {
      requireArgs('COUNTA', args, 1, null);
      let n = 0;
      for (let i = 0; i < values.length; i += 1) {
        const flat = flatValues(values[i], ctx, []);
        for (let j = 0; j < flat.length; j += 1) {
          if (flat[j] !== null && flat[j] !== undefined) n += 1;
        }
      }
      return n;
    }
    case 'ABS': {
      requireArgs('ABS', args, 1, 1);
      return Math.abs(toNumber(deRange(values[0], ctx)));
    }
    case 'ROUND':
    case 'ROUNDUP':
    case 'ROUNDDOWN': {
      requireArgs(name, args, 1, 2);
      const n = toNumber(deRange(values[0], ctx));
      const d = values.length > 1 && args[1].type !== 'missing' ? toNumber(deRange(values[1], ctx)) : 0;
      if (name === 'ROUND') return roundHalfAway(n, d);
      if (name === 'ROUNDUP') return roundAwayCeil(n, d);
      return roundTowardZero(n, d);
    }
    case 'SUMPRODUCT': {
      requireArgs('SUMPRODUCT', args, 1, null);
      const arrays = [];
      let length = null;
      for (let i = 0; i < values.length; i += 1) {
        const flat = flatValues(values[i], ctx, []);
        for (let j = 0; j < flat.length; j += 1) {
          if (isErrorBox(flat[j])) throw new ErrorSignal(flat[j].__error);
        }
        if (length === null) length = flat.length;
        else if (flat.length !== length) raiseError('#VALUE!');
        arrays.push(flat);
      }
      let total = 0;
      for (let j = 0; j < length; j += 1) {
        let p = 1;
        for (let i = 0; i < arrays.length; i += 1) {
          const v = arrays[i][j];
          p *= typeof v === 'number' ? v : 0;
        }
        total += p;
      }
      return total;
    }
    case 'AND':
    case 'OR': {
      requireArgs(name, args, 1, null);
      const bools = [];
      for (let i = 0; i < values.length; i += 1) {
        const flat = flatValues(values[i], ctx, []);
        for (let j = 0; j < flat.length; j += 1) {
          const v = flat[j];
          if (isErrorBox(v)) throw new ErrorSignal(v.__error);
          if (v === null || v === undefined) continue;
          if (typeof v === 'string' && isRangeBox(values[i])) continue;
          bools.push(toBoolean(v));
        }
      }
      if (bools.length === 0) raiseError('#VALUE!');
      if (name === 'AND') {
        for (let i = 0; i < bools.length; i += 1) if (!bools[i]) return false;
        return true;
      }
      for (let i = 0; i < bools.length; i += 1) if (bools[i]) return true;
      return false;
    }
    case 'NOT': {
      requireArgs('NOT', args, 1, 1);
      return !toBoolean(deRange(values[0], ctx));
    }
    case 'VLOOKUP': return doVlookup(values, args, ctx);
    case 'INDEX': return doIndex(values, args, ctx);
    case 'MATCH': return doMatch(values, args, ctx);
    default:
      raiseUnsupported('function', name, 'function not in the plan §6.3 set');
      return null;
  }
}

function rangeOf(v, fnName) {
  if (!isRangeBox(v)) {
    raiseUnsupported('argument_type', fnName, fnName + ' needs a range argument');
  }
  return v.__range;
}

function doVlookup(values, args, ctx) {
  requireArgs('VLOOKUP', args, 3, 4);
  const needle = deRange(values[0], ctx);
  if (isErrorBox(needle)) throw new ErrorSignal(needle.__error);
  const range = rangeOf(values[1], 'VLOOKUP');
  const colIndex = Math.trunc(toNumber(deRange(values[2], ctx)));
  const approximate = values.length < 4 || args[3].type === 'missing' ? true : toBoolean(deRange(values[3], ctx));
  const width = range.c2 - range.c1 + 1;
  if (colIndex < 1) raiseError('#VALUE!');
  if (colIndex > width) raiseError('#REF!');

  let bestRow = null;
  for (let r = range.r1; r <= range.r2; r += 1) {
    const key = ctx.getValue(range.sheet, r, range.c1, ctx.note);
    if (isErrorBox(key)) throw new ErrorSignal(key.__error);
    if (!approximate) {
      if (valuesEqual(key, needle)) { bestRow = r; break; }
    } else {
      if (key === null || key === undefined) continue;
      if (compareForLookup(key, needle) <= 0) bestRow = r;
      else break;
    }
  }
  if (bestRow === null) raiseError('#N/A');
  return ctx.getValue(range.sheet, bestRow, range.c1 + colIndex - 1, ctx.note);
}

function doIndex(values, args, ctx) {
  requireArgs('INDEX', args, 2, 3);
  const range = rangeOf(values[0], 'INDEX');
  const height = range.r2 - range.r1 + 1;
  const width = range.c2 - range.c1 + 1;
  const a = Math.trunc(toNumber(deRange(values[1], ctx)));
  const hasCol = values.length > 2 && args[2].type !== 'missing';
  let rowNum;
  let colNum;
  if (hasCol) {
    rowNum = a;
    colNum = Math.trunc(toNumber(deRange(values[2], ctx)));
  } else if (height === 1 && width > 1) {
    rowNum = 1;
    colNum = a;
  } else {
    rowNum = a;
    colNum = 1;
  }
  if (rowNum < 1 || colNum < 1 || rowNum > height || colNum > width) raiseError('#REF!');
  return ctx.getValue(range.sheet, range.r1 + rowNum - 1, range.c1 + colNum - 1, ctx.note);
}

function doMatch(values, args, ctx) {
  requireArgs('MATCH', args, 2, 3);
  const needle = deRange(values[0], ctx);
  if (isErrorBox(needle)) throw new ErrorSignal(needle.__error);
  const range = rangeOf(values[1], 'MATCH');
  const height = range.r2 - range.r1 + 1;
  const width = range.c2 - range.c1 + 1;
  if (height !== 1 && width !== 1) raiseError('#N/A');
  const matchType = values.length > 2 && args[2].type !== 'missing' ? Math.trunc(toNumber(deRange(values[2], ctx))) : 1;
  const count = height === 1 ? width : height;
  let best = null;
  for (let i = 0; i < count; i += 1) {
    const r = height === 1 ? range.r1 : range.r1 + i;
    const c = height === 1 ? range.c1 + i : range.c1;
    const v = ctx.getValue(range.sheet, r, c, ctx.note);
    if (isErrorBox(v)) throw new ErrorSignal(v.__error);
    if (matchType === 0) {
      if (valuesEqual(v, needle)) return i + 1;
    } else if (matchType > 0) {
      if (v === null || v === undefined) continue;
      if (compareForLookup(v, needle) <= 0) best = i + 1;
      else break;
    } else {
      if (v === null || v === undefined) continue;
      if (compareForLookup(v, needle) >= 0) best = i + 1;
      else break;
    }
  }
  if (best === null) raiseError('#N/A');
  return best;
}

/**
 * Exact-match equality for VLOOKUP/MATCH: text is case-insensitive (Excel's
 * rule) and a blank cell equals 0 or "" but nothing else.
 */
function valuesEqual(a, b) {
  const x = a === undefined ? null : a;
  const y = b === undefined ? null : b;
  if (typeof x === 'string' && typeof y === 'string') return x.toUpperCase() === y.toUpperCase();
  if (x === null && y === null) return true;
  if (x === null) return y === 0 || y === '';
  if (y === null) return x === 0 || x === '';
  return x === y;
}

function compareForLookup(a, b) {
  if (typeof a === 'number' && typeof b === 'number') return a < b ? -1 : (a > b ? 1 : 0);
  const sa = typeof a === 'string' ? a.toUpperCase() : String(a);
  const sb = typeof b === 'string' ? b.toUpperCase() : String(b);
  if (typeof a === 'number' || typeof b === 'number') {
    if (typeRank(a) !== typeRank(b)) return typeRank(a) < typeRank(b) ? -1 : 1;
  }
  return sa < sb ? -1 : (sa > sb ? 1 : 0);
}

export default {
  FORMULA_ENGINE_VERSION: FORMULA_ENGINE_VERSION,
  ERROR_VALUES: ERROR_VALUES,
  SUPPORTED_FUNCTIONS: SUPPORTED_FUNCTIONS,
  AGGREGATE_FUNCTIONS: AGGREGATE_FUNCTIONS,
  FormulaEngineError: FormulaEngineError,
  FormulaParseError: FormulaParseError,
  columnLetterToIndex: columnLetterToIndex,
  columnIndexToLetter: columnIndexToLetter,
  formatCellRef: formatCellRef,
  parseCellRef: parseCellRef,
  parseRangeRef: parseRangeRef,
  expandRange: expandRange,
  isErrorValue: isErrorValue,
  tokenizeFormula: tokenizeFormula,
  parseFormula: parseFormula,
  walkFormulaAst: walkFormulaAst,
  collectReferences: collectReferences,
  scanUnsupported: scanUnsupported,
  cellHasFormula: cellHasFormula,
  cellStoredValue: cellStoredValue,
  cellCachedValue: cellCachedValue,
  createWorkbookContext: createWorkbookContext,
  evaluateFormula: evaluateFormula,
  evaluateParsed: evaluateParsed,
};
